/**
 * Per-page metadata. React 19 hoists <title>, <meta> and <link> rendered here into the
 * document <head>, so they are present in the server-rendered HTML and update on
 * client navigation. The root route intentionally sets no title/description/canonical/
 * robots/social tags, so each page emits exactly one set.
 */
interface SEOHeadProps {
  title: string;
  description: string;
  path: string;
  ogImage?: string;
  keywords?: string;
  jsonLd?: object | object[];
  breadcrumbs?: { name: string; path: string }[];
  noindex?: boolean;
}

export const SITE_URL = "https://netius.com";

/** Serialize JSON-LD safely for inline <script> output. */
export const serializeJsonLd = (data: unknown) => JSON.stringify(data).replace(/</g, "\\u003c");

const SEOHead = ({
  title,
  description,
  path,
  ogImage = "/og-image.png",
  keywords,
  jsonLd,
  breadcrumbs,
  noindex,
}: SEOHeadProps) => {
  const fullUrl = `${SITE_URL}${path}`;
  const fullTitle = path === "/" ? title : `${title} | NETIUS`;
  const imageUrl = `${SITE_URL}${ogImage}`;

  const breadcrumbLd =
    breadcrumbs && breadcrumbs.length > 0
      ? {
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: breadcrumbs.map((b, i) => ({
            "@type": "ListItem",
            position: i + 1,
            name: b.name,
            item: `${SITE_URL}${b.path}`,
          })),
        }
      : null;

  const allLd: object[] = [];
  if (jsonLd) allLd.push(...(Array.isArray(jsonLd) ? jsonLd : [jsonLd]));
  if (breadcrumbLd) allLd.push(breadcrumbLd);

  return (
    <>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={fullUrl} />
      <meta
        name="robots"
        content={noindex ? "noindex, nofollow" : "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1"}
      />

      {/* Open Graph */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={fullUrl} />
      <meta property="og:image" content={imageUrl} />
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="NETIUS" />
      <meta property="og:locale" content="en_US" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={imageUrl} />
      <meta name="twitter:site" content="@netius" />
      {keywords && <meta name="keywords" content={keywords} />}
      {allLd.length > 0 && (
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: serializeJsonLd(allLd) }} />
      )}
    </>
  );
};

export default SEOHead;
